import React, {
  useContext,
  createContext,
  useState,
  MouseEventHandler,
  useEffect,
  useRef
} from "react";
import { AuthCodeMSALBrowserAuthenticationProvider } from "@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser";
import { InteractionType, PublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useNavigate, useLocation } from "react-router-dom";

import { getUser } from "../services/GraphService";
import config from "../services/Config";

export interface AppUser {
  displayName?: string;
  email?: string;
  avatar?: string;
  timeZone?: string;
  timeFormat?: string;
  admin?: boolean;
}

export interface AppError {
  message: string;
  debug?: string;
}

type AppContext = {
  user?: AppUser;
  error?: AppError;
  signIn?: MouseEventHandler<HTMLElement>;
  signOut?: MouseEventHandler<HTMLElement>;
  displayError?: Function;
  clearError?: Function;
  authProvider?: AuthCodeMSALBrowserAuthenticationProvider;
  requestsCount: number;
  setRequestsCount: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean;
  isSessionReady: boolean;
};

const appContext = createContext<AppContext>({
  user: undefined,
  error: undefined,
  signIn: undefined,
  signOut: undefined,
  displayError: undefined,
  clearError: undefined,
  authProvider: undefined,
  requestsCount: 0,
  setRequestsCount: () => {},
  loading: true,
  isSessionReady: false
});

export function useAppContext(): AppContext {
  return useContext(appContext);
}

interface ProvideAppContextProps {
  children: React.ReactNode;
}

export default function ProvideAppContext({
  children,
}: ProvideAppContextProps) {
  const auth = useProvideAppContext();
  return <appContext.Provider value={auth}>{children}</appContext.Provider>;
}

function useProvideAppContext() {
  const initialized = useRef(false);
  const msal = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<AppUser | undefined>(undefined);
  const [error, setError] = useState<AppError | undefined>(undefined);
  const [authProvider, setAuthProvider] = useState<AuthCodeMSALBrowserAuthenticationProvider | undefined>(undefined);
  const [requestsCount, setRequestsCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [isSessionReady, setIsSessionReady] = useState(false);

  const displayError = (message: string, debug?: string) => {
    setError({ message, debug });
  };

  const clearError = () => {
    setError(undefined);
  };

  const initializeMsal = async () => {
    try {
      // Inicializar MSAL
      await msal.instance.initialize();
      // Manejar la redirección si hay una
      await msal.instance.handleRedirectPromise();
      // Verificar si hay una cuenta activa
      const account = msal.instance.getActiveAccount();
      if (account) {
        // Configurar el proveedor de autenticación
        const provider = new AuthCodeMSALBrowserAuthenticationProvider(
          msal.instance as PublicClientApplication,
          {
            account: account,
            scopes: config.scopes,
            interactionType: InteractionType.Popup,
          }
        );
        setAuthProvider(provider);
        // Obtener el usuario desde Microsoft Graph
        const graphUser = await getUser(provider);
        const appUser: AppUser = {
          displayName: graphUser.displayName || "",
          email: graphUser.mail || graphUser.userPrincipalName || "",
        };
        setUser(appUser);
        // Enviar el usuario al backend y luego iniciar sesión
        await sendUserToBackend(appUser);
        await loginUserToBackend(appUser.email || "");
        // Obtener información adicional del usuario desde el backend
        const backendUser = await fetchUserProfile();
        const validateUser = { ...appUser, ...backendUser };
        setUser(validateUser)
        // Consultar conteo de solicitudes una vez que se tiene el perfil si es admin
        if (validateUser.admin) {
          await fetchRequestsCount();
        }
      }
    } catch (error: any) {
      displayError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const sendUserToBackend = async (user: AppUser) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to save user");
      }
    } catch (error: any) {
      displayError(error.message);
    }
  };

  const signIn = async () => {
    try {
      await msal.instance.loginPopup({
        scopes: config.scopes,
        prompt: "select_account",
      });

      const account = msal.instance.getActiveAccount();
      if (account) {
        const provider = new AuthCodeMSALBrowserAuthenticationProvider(
          msal.instance as PublicClientApplication,
          {
            account: account,
            scopes: config.scopes,
            interactionType: InteractionType.Popup,
          }
        );
        setAuthProvider(provider);

        // Obtener el usuario desde Microsoft Graph
        const user = await getUser(provider);

        setUser({
          displayName: user.displayName || "",
          email: user.mail || user.userPrincipalName || "",
        });

        navigate("/");

      // Manualmente volver a correr la lógica de inicialización
      await initializeMsal();
      }
    } catch (error: any) {
      displayError(error.message);
    }
  };

  const loginUserToBackend = async (email: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
        credentials: "include",
      });

      if (response.ok) {
        setIsSessionReady(true); 
      } else {
        throw new Error("Failed to login user");
      }
    } catch (error: any) {
      displayError(error.message);
    }
  };

    const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
  
      if (!response.ok) {
        throw new Error("Failed to fetch user profile");
      }
  
      const userProfile = await response.json();
      return { admin: userProfile.admin };
    } catch (error: any) {
      displayError(error.message);
      return { admin: false };
    }
  };

  const fetchRequestsCount = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/getRequestsCount`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setRequestsCount(data.count);
      } else {
        console.error('Error al obtener el número de solicitudes');
      }
    } catch (error: any) {
      console.error('Error de red al obtener el número de solicitudes', error);
    }
  };

  const signOut = async () => {
    try {
      await msal.instance.logoutPopup();

      if (user && user.email) {
        await logoutUserToBackend(user.email);
      }

      setUser(undefined);
    } catch (error: any) {
      displayError(error.message);
    }
  };

  const logoutUserToBackend = async (email: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to logout user from backend");
      }
    } catch (error: any) {
      displayError(error.message);
    }
  };

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    initializeMsal();
  }, []);

  useEffect(() => {
    if (user?.admin) {
      fetchRequestsCount();
    }
  }, [location.pathname]);

  return {
    user,
    error,
    signIn,
    signOut,
    displayError,
    clearError,
    authProvider,
    requestsCount,
    setRequestsCount,
    loading,
    isSessionReady
  };
}